import { useEvent } from '@emerald-works/react-event-bus-client'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { notificationsSlice } from '../../../reducers'
import { NOTIFICATIONS_API_ENABLED, NOTIFICATIONS_PAGE_SIZE } from '../constants'
import { buildMockNotifications } from '../mock-notifications'

/** Fake round-trip so the loading state is actually exercised in mock mode. */
const MOCK_LATENCY_MS = 700

/**
 * Owns the notification centre's data. Reads through the `notifications` slice
 * so every mounted bell shares one source of truth (desktop and mobile bells
 * are mutually exclusive today, but state living in the store rather than in
 * component state is what makes the unread badge correct across the app).
 *
 * There is no backend yet, so the hook serves mock data through that same path
 * — see NOTIFICATIONS_API_ENABLED in ../constants.js.
 */
export const useNotifications = () => {
  const dispatch = useDispatch()
  const store = useStore()
  const { actions, selectors, eventBus } = notificationsSlice

  const notifications = useSelector(selectors.selectNotifications)
  const unreadCount = useSelector(selectors.selectUnreadCount)
  const loading = useSelector(selectors.selectLoading)
  const error = useSelector(selectors.selectError)
  const hasLoaded = useSelector(selectors.selectHasLoaded)
  const needsResync = useSelector(selectors.selectNeedsResync)
  const loadingMore = useSelector(selectors.selectLoadingMore)
  const hasMore = useSelector(selectors.selectHasMore)
  const preferences = useSelector(selectors.selectPreferences)

  const [getNotifications, markNotificationRead, markAllNotificationsRead] = useEvent([
    eventBus.getNotifications,
    eventBus.markNotificationRead,
    eventBus.markAllNotificationsRead
  ])

  const mockTimer = useRef(null)
  useEffect(() => () => clearTimeout(mockTimer.current), [])

  /**
   * One request shape for both the first page and "Load more" — the loading
   * action dispatched here is what tells the reducer which it is (the event
   * carries no `onStart`; see reducers/notifications/event-bus.js).
   */
  const fetchPage = useCallback(({ from }) => {
    // One request at a time, whoever asks: the bell and the /notifications page
    // each own an instance of this hook and mount in the same commit, so both
    // would otherwise fire the same fetch. Read off the store because the
    // rendered `loading` is still false inside that commit.
    const state = store.getState()
    if (selectors.selectLoading(state) || selectors.selectLoadingMore(state)) return

    dispatch(from > 0 ? actions.setLoadingMore() : actions.setLoading())
    const params = { from, size: NOTIFICATIONS_PAGE_SIZE }

    if (NOTIFICATIONS_API_ENABLED) {
      // Preferences are not sent: the server knows them and decides what to
      // send (NC-52). Only the mock needs telling.
      getNotifications.trigger(params)
      return
    }
    // MOCK PATH — dispatches the same actions the event bus would, so the
    // loading -> success transition is real even without a backend.
    clearTimeout(mockTimer.current)
    mockTimer.current = setTimeout(() => {
      dispatch(actions.setNotifications(buildMockNotifications({ ...params, preferences })))
    }, MOCK_LATENCY_MS)
  }, [dispatch, actions, getNotifications, preferences, selectors, store])

  const load = useCallback(() => fetchPage({ from: 0 }), [fetchPage])

  // Appends the next page (NC-31). `from` is how much is already held rather
  // than a page counter, so a dismissed row can't shift the window. Read off
  // the store, so a double-click in one frame can't slip past a stale guard.
  const loadMore = useCallback(() => {
    const state = store.getState()
    if (!selectors.selectHasMore(state)) return
    fetchPage({ from: selectors.selectNotifications(state).length })
  }, [fetchPage, selectors, store])

  // Eager, so the unread badge is correct before the panel is ever opened.
  useEffect(() => {
    if (hasLoaded) return
    if (selectors.selectHasLoaded(store.getState())) return
    load()
  }, [store, selectors, load, hasLoaded])

  // Turning a type off changes what the server would send, so the list is
  // re-fetched rather than filtered here (NC-40).
  const previousPreferencesRef = useRef(preferences)
  useEffect(() => {
    if (previousPreferencesRef.current === preferences) return
    previousPreferencesRef.current = preferences
    load()
  }, [preferences, load])

  // An optimistic write that failed on the server leaves this client showing
  // something the backend never accepted — pull the truth back down.
  useEffect(() => {
    if (needsResync) load()
  }, [needsResync, load])

  // Read/dismiss update the store first and tell the server after — the user
  // never waits on a round trip to see a dot clear.
  const markRead = useCallback(
    notification => {
      const id = notification?.id ?? notification
      if (!id) return
      dispatch(actions.markRead(id))
      if (NOTIFICATIONS_API_ENABLED) markNotificationRead.trigger({ id })
    },
    [dispatch, actions, markNotificationRead]
  )

  const markAllRead = useCallback(() => {
    dispatch(actions.markAllRead())
    if (NOTIFICATIONS_API_ENABLED) markAllNotificationsRead.trigger()
  }, [dispatch, actions, markAllNotificationsRead])

  return {
    notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    error,
    hasLoaded,
    load,
    loadMore,
    markRead,
    markAllRead
  }
}

export default useNotifications
